/**
 * MoneyTotals — fila horizontal de mini-stats (Neto / impuesto / [Total moneda + TC] / Total)
 * reutilizable en el header de la Vista 360 y del Formulario 360.
 *
 * Es presentacional: recibe los stats ya calculados. El helper `buildDocMoneyStats`
 * arma los stats típicos de un documento de venta a partir de total / exchange_rate_data
 * / outstanding_amount. Theming con tokens semánticos (light + dark).
 */
import { useTenantLocale, useMoney, tenantTax } from '@/lib/locale'

export interface MoneyStat {
  label: string
  value: string
  /** 'main' destaca el total (tipografía grande); 'fx' lo pinta en color de marca. */
  variant?: 'default' | 'main' | 'fx'
  /** Línea secundaria pequeña debajo del valor (ej: tasa de cambio). */
  sub?: string
}

interface MoneyTotalsProps {
  stats: MoneyStat[]
  className?: string
}

export function MoneyTotals({ stats, className = '' }: MoneyTotalsProps) {
  return (
    <div className={`flex items-end flex-wrap gap-y-2 ${className}`}>
      {stats.map((s, i) => (
        <div
          key={`${s.label}-${i}`}
          className="flex flex-col items-end px-4 border-r border-divider last:pr-0 last:border-r-0"
        >
          <span className="text-[10.5px] uppercase tracking-wide text-content-subtle whitespace-nowrap">
            {s.label}
          </span>
          <span
            className={[
              'tabular-nums font-bold whitespace-nowrap mt-1',
              s.variant === 'main' ? 'text-2xl font-extrabold -tracking-[0.5px]' : 'text-[14.5px]',
              s.variant === 'fx' ? 'text-primary' : 'text-content',
            ].join(' ')}
          >
            {s.value}
          </span>
          {s.sub && (
            <span className="text-[11px] text-content-subtle whitespace-nowrap mt-0.5">{s.sub}</span>
          )}
        </div>
      ))}
    </div>
  )
}

// ─── Helper: stats típicos de un documento ────────────────────────────────────

interface DocLike {
  subtotal?: string | number
  tax_amount?: string | number
  total?: string | number
  outstanding_amount?: string | number
  /** { USD: "894.99", locked_at: "...", ... } */
  exchange_rate_data?: Record<string, unknown> | null
}

const CURRENCY_KEYS = new Set(['USD', 'EUR', 'UF', 'UTM'])

/**
 * Formatea una tasa de cambio en CLP preservando decimales. El formateador CLP
 * (useClpFormatter) redondea a entero, lo que esconde los decimales de la tasa
 * (ej. 1 USD = $894,99 se veía como $895). Las divisas/UF suelen traer 2-4.
 */
export function formatExchangeRate(rate: number | string, locale = 'es-CL'): string {
  return `$${(Number(rate) || 0).toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  })}`
}

/**
 * Arma los stats de un documento: Neto, impuesto, [Total moneda + TC], Total (main),
 * y Saldo si está pendiente. `fmt` formatea CLP según el locale del tenant.
 */
export function buildDocMoneyStats(
  doc: DocLike,
  fmt: (v: string | number) => string,
): MoneyStat[] {
  const stats: MoneyStat[] = []
  if (doc.subtotal != null) stats.push({ label: 'Neto', value: fmt(doc.subtotal) })
  // El nombre del impuesto sale del país del tenant: «IVA» en Chile, «IGV» en
  // Perú. Es una función pura, así que usa `tenantTax()` y no el hook.
  if (doc.tax_amount != null) stats.push({ label: tenantTax().name, value: fmt(doc.tax_amount) })

  // Moneda extranjera: si hay tasa para una moneda no-CLP, muestra el NETO en
  // esa moneda (el dato relevante para el cliente; el Total CLP queda como main).
  const fx = doc.exchange_rate_data || {}
  const fxCode = Object.keys(fx).find((k) => CURRENCY_KEYS.has(k))
  const rate = fxCode ? Number(fx[fxCode]) : 0
  if (fxCode && rate > 0 && doc.subtotal != null) {
    const foreignNeto = Number(doc.subtotal) / rate
    stats.push({
      label: `Neto ${fxCode}`,
      value: `${fxCode} ${foreignNeto.toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      variant: 'fx',
      sub: `1 ${fxCode} = ${formatExchangeRate(rate)}`,
    })
  }

  if (doc.total != null) stats.push({ label: 'Total', value: fmt(doc.total), variant: 'main' })
  const outstanding = doc.outstanding_amount != null ? Number(doc.outstanding_amount) : 0
  if (outstanding > 0) {
    stats.push({ label: 'Saldo', value: fmt(doc.outstanding_amount as string) })
  }
  return stats
}

/**
 * Hook de conveniencia: formateador de montos ligado al tenant.
 *
 * ⚠️ Tenía la moneda **CLP fija** y forzaba 0 decimales, así que un tenant
 * peruano veía sus soles con el signo chileno y **sin céntimos**. Ahora delega en
 * `useMoney`, que resuelve locale y moneda del tenant y deja que `Intl` decida
 * los decimales — CLP no lleva, PEN y USD llevan dos. Mismo criterio del #1016.
 *
 * El nombre se conserva para no tocar sus consumidores; lo que cambia es que ya
 * no es «CLP» sino la moneda que corresponda.
 */
export function useClpFormatter() {
  return useMoney()
}
